import { useEffect, useState } from 'react'
import { RefreshCw } from 'lucide-react'

/** "updated N min ago" relative label from an ISO timestamp. */
export function freshnessLabel(generatedAt?: string): string {
  if (!generatedAt) return ''
  const ms = Date.now() - new Date(generatedAt).getTime()
  if (!Number.isFinite(ms) || ms < 60_000) return 'updated just now'
  const min = Math.floor(ms / 60_000)
  if (min < 60) return `updated ${min} min ago`
  const h = Math.floor(min / 60)
  if (h < 24) return `updated ${h}h ago`
  const d = Math.floor(h / 24)
  return `updated ${d}d ago`
}

/**
 * Freshness label + manual refresh button for the live dashboards. Reads the
 * snapshot's `generatedAt` (server-stamped, honours the 10-min TTL) and lets
 * the user force a refetch (the server still serves its cached snapshot until
 * the TTL lapses). Re-renders every 30s so the relative label keeps ageing.
 */
export default function DashboardFreshness({
  generatedAt,
  onRefresh,
}: {
  generatedAt?: string
  onRefresh: () => void | Promise<void>
}) {
  const [, setTick] = useState(0)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30_000)
    return () => clearInterval(t)
  }, [])

  const handle = async () => {
    if (busy) return
    setBusy(true)
    try {
      await onRefresh()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex items-center gap-2 shrink-0">
      {generatedAt && (
        <span className="text-[11px] text-muted-foreground tabular-nums">{freshnessLabel(generatedAt)}</span>
      )}
      <button
        type="button"
        onClick={handle}
        disabled={busy}
        title="Refresh"
        aria-label="Refresh"
        className="inline-flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition disabled:opacity-50"
      >
        <RefreshCw size={14} className={busy ? 'animate-spin' : ''} />
      </button>
    </div>
  )
}
